/**
 * Keeping the key log checked, and remembering where we got to.
 *
 * A log nobody verifies is decoration. This is the part that runs: fetch what
 * has been appended since last time, walk the chain, and store the head we
 * reached. The head is the whole memory — it is what makes the NEXT check able
 * to say "this does not continue from what you showed me before".
 *
 * A failure is deliberately sticky. The server that rewrote its history is the
 * server answering the next request too, and a warning that clears itself on
 * the following poll is a warning nobody will ever see.
 */
import { verifyLog, GENESIS } from './keyLog.mjs';
import { recordOwnHeads, resolvePendingGossip } from './keyLogGossip.mjs';

const STATE_KEY = 'keylog:state';     // { seq, head }
const ALARM_KEY = 'keylog:alarm';     // { error, at, seenAt }

export async function checkKeyLog(store, api, { pages = 8 } = {}) {
  const held = (await store.getValue(STATE_KEY)) || { seq: 0, head: GENESIS };
  let state = held;
  let added = [];

  for (let i = 0; i < pages; i++) {
    const page = await api.keyLog(state.seq);
    const r = await verifyLog(state, page);
    if (!r.ok) {
      // Keep the last head we could vouch for. Advancing past a break would
      // mean adopting the rewritten history as our new baseline, and the next
      // check would find it perfectly consistent.
      const alarm = { error: r.error, at: r.at, seenAt: new Date().toISOString(), head: state.head };
      await store.setValue(ALARM_KEY, alarm);
      return { ok: false, ...alarm, state };
    }
    state = r.state;
    added = added.concat(r.added || []);
    if (!page.hasMore) break;
  }

  await store.setValue(STATE_KEY, state);

  // Gossip bookkeeping: remember the head at every seq we just verified, and
  // re-check the claims peers made about entries we had not reached yet. A
  // claim that our own chain now contradicts is a split view — the chain
  // itself is fine, which is exactly the point: each chain alone always is.
  await recordOwnHeads(store, added, state);
  const splitAlarm = await resolvePendingGossip(store);
  if (splitAlarm) return { ok: false, ...splitAlarm, state };

  return { ok: true, state, added, firstRun: held.seq === 0 };
}

/** The alarm outlives the check that raised it, and only a person clears it. */
export async function keyLogAlarm(store) {
  return (await store.getValue(ALARM_KEY)) || null;
}

export async function clearKeyLogAlarm(store) {
  await store.deleteValue(ALARM_KEY);
}

export { STATE_KEY, ALARM_KEY };
